// API client for secure authentication - replaces direct Supabase calls
// Implements Frontend → API → Backend architecture with zero frontend secrets

import { buildSupabaseRequestHeaders } from './supabaseHeaders'
import { clearBrowserAuthState, clearBrowserSessionState } from '../services/browserState'

class AuthAPIClient {
  constructor() {
    // Using Supabase Edge Functions for authentication API
    // Build URL from environment variables - no hardcoded secrets
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
    const supabasePublishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY
    
    if (!supabaseUrl) {
      throw new Error('REACT_APP_SUPABASE_URL environment variable is required')
    }
    if (!supabasePublishableKey) {
      throw new Error('REACT_APP_SUPABASE_PUBLISHABLE_KEY environment variable is required')
    }
    
    this.baseURL = supabaseUrl.replace(/\/$/, '') + '/functions/v1'
    this.supabasePublishableKey = supabasePublishableKey
    this.token = null
    this.refreshToken = null
    this.userId = null
    this.authoritySource = null
    
    // Circuit breaker state for network resilience
    this.circuitBreaker = {
      failures: 0,
      lastFailureTime: null,
      isOpen: false,
      openDuration: 30000, // 30 seconds
      maxFailures: 3
    }
    
    // Sessions are memory-only. Remove exact legacy copies without reading them.
    this.loadTokensFromStorage()
  }

  /**
   * Reject stored browser tokens and start with an empty in-memory session.
   */
  loadTokensFromStorage() {
    this.token = null
    this.refreshToken = null
    this.userId = null
    this.authoritySource = null
    clearBrowserAuthState()
  }

  /**
   * Keep tokens in memory only.
   */
  saveTokensToStorage(accessToken, refreshToken, userId = null, authoritySource = null) {
    if (accessToken) this.token = accessToken
    if (refreshToken) this.refreshToken = refreshToken
    if (userId) this.userId = userId
    if (authoritySource) this.authoritySource = authoritySource
  }

  clearMemoryTokens() {
    this.token = null
    this.refreshToken = null
    this.userId = null
    this.authoritySource = null
  }

  /**
   * Clear memory and the exact reviewed prior-user browser state.
   */
  clearTokens() {
    this.clearMemoryTokens()
    clearBrowserSessionState()
  }

  /**
   * Check if error is a network-level error (not authentication)
   */
  isNetworkError(error) {
    const networkErrors = [
      'Failed to fetch',
      'NetworkError',
      'ERR_NETWORK_CHANGED',
      'ERR_NAME_NOT_RESOLVED',
      'ERR_INTERNET_DISCONNECTED',
      'ERR_CONNECTION_REFUSED'
    ]
    
    return networkErrors.some(netError => 
      error.message?.includes(netError) || 
      error.name?.includes(netError) ||
      error.toString().includes(netError)
    )
  }

  /**
   * Circuit breaker management for network resilience
   */
  updateCircuitBreaker(error) {
    if (this.isNetworkError(error)) {
      this.circuitBreaker.failures++
      this.circuitBreaker.lastFailureTime = Date.now()
      
      if (this.circuitBreaker.failures >= this.circuitBreaker.maxFailures) {
        this.circuitBreaker.isOpen = true
        console.log(`🔐 AuthAPI: Circuit breaker OPEN - ${this.circuitBreaker.failures} consecutive network failures`)
      }
    } else {
      // Reset on successful non-network operation
      this.resetCircuitBreaker()
    }
  }

  resetCircuitBreaker() {
    this.circuitBreaker.failures = 0
    this.circuitBreaker.lastFailureTime = null
    this.circuitBreaker.isOpen = false
  }

  checkCircuitBreaker() {
    if (!this.circuitBreaker.isOpen) return true

    const timeSinceLastFailure = Date.now() - this.circuitBreaker.lastFailureTime
    if (timeSinceLastFailure > this.circuitBreaker.openDuration) {
      console.log('🔐 AuthAPI: Circuit breaker HALF-OPEN - attempting recovery')
      this.circuitBreaker.isOpen = false
      return true
    }

    return false
  }

  /**
   * Retry logic with exponential backoff
   */
  async retryWithBackoff(fn, maxRetries = 3, baseDelay = 1000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn()
      } catch (error) {
        // Only retry on network errors, not auth errors
        if (attempt === maxRetries || !this.isNetworkError(error)) {
          throw error
        }
        
        const delay = baseDelay * Math.pow(2, attempt - 1)
        console.log(`🔐 AuthAPI: Network error, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  /**
   * Make authenticated API request with network resilience
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    const { headers: additionalHeaders = {}, ...requestOptions } = options
    
    const config = {
      ...requestOptions,
      headers: buildSupabaseRequestHeaders(
        this.supabasePublishableKey,
        this.token && !options.skipAuth ? this.token : null,
        additionalHeaders
      )
    }

    const isFormData = typeof FormData !== 'undefined' && config.body instanceof FormData

    if (isFormData) {
      delete config.headers['Content-Type']
    } else if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body)
    }

    // Wrap fetch in retry logic for network resilience
    const makeFetchRequest = async () => {
      const response = await fetch(url, config)
      
      // Handle 401 responses - token may be expired
      if (response.status === 401 && this.refreshToken && !options.skipTokenRefresh) {
        console.log('🔐 AuthAPI: Access token expired, attempting refresh...')
        
        const refreshResult = await this.refreshSession()
        if (refreshResult.session) {
          // Retry the original request with new token
          return this.makeRequest(endpoint, { ...options, skipTokenRefresh: true })
        } else {
          // Refresh failed - clear tokens and throw error
          this.clearTokens()
          throw new Error('Session expired and refresh failed')
        }
      }

      // Parse response
      let data
      try {
        data = await response.json()
      } catch (parseError) {
        data = { error: 'Invalid response format' }
      }

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`)
      }

      return data
    }

    try {
      // Check circuit breaker before making request
      if (!this.checkCircuitBreaker()) {
        const error = new Error('Service temporarily unavailable due to network issues')
        error.isNetworkError = true
        error.isCircuitBreakerOpen = true
        throw error
      }

      // Apply retry logic only for critical auth endpoints
      const criticalEndpoints = ['/auth-session', '/auth-login', '/auth-signup']
      const shouldRetry = criticalEndpoints.some(criticalEndpoint => 
        endpoint.includes(criticalEndpoint)
      )

      let result
      if (shouldRetry) {
        result = await this.retryWithBackoff(makeFetchRequest, 3, 1000)
      } else {
        result = await makeFetchRequest()
      }

      // Reset circuit breaker on successful request
      this.resetCircuitBreaker()
      return result

    } catch (error) {
      // Update circuit breaker on error
      this.updateCircuitBreaker(error)

      // Enhanced error logging with network error detection
      const isNetworkIssue = this.isNetworkError(error)
      console.error(`AuthAPI request failed (${endpoint}):`, {
        error: error.message,
        isNetworkError: isNetworkIssue,
        circuitBreakerOpen: this.circuitBreaker.isOpen,
        consecutiveFailures: this.circuitBreaker.failures,
        endpoint: url
      })
      
      // Throw enhanced error with context
      const enhancedError = new Error(
        isNetworkIssue 
          ? `Network connectivity issue: ${error.message}` 
          : error.message
      )
      enhancedError.isNetworkError = isNetworkIssue
      enhancedError.isCircuitBreakerOpen = this.circuitBreaker.isOpen
      enhancedError.originalError = error
      
      throw enhancedError
    }
  }

  /**
   * Sign up new user
   */
  async signUp(email, password) {
    try {
      console.log('🔐 AuthAPI: Attempting signup for:', email)
      
      const response = await this.makeRequest('/auth-signup', {
        method: 'POST',
        body: { email, password },
        skipAuth: true // No auth needed for signup
      })

      // Save tokens if signup was successful and returned a session
      if (response.session?.access_token) {
        this.saveTokensToStorage(
          response.session.access_token,
          response.session.refresh_token,
          response.user?.id,
          'custom'
        )
        
        // CRITICAL: Immediately trigger auth state change for UI update
        if (this._authCallback) {
          console.log('🔐 AuthAPI: Triggering auth state change after signup')
          // Include user data in session object for the callback
          const sessionWithUser = {
            ...response.session,
            user: response.user
          }
          await this._authCallback('SIGNED_IN', sessionWithUser)
        }
      }

      console.log('🔐 AuthAPI: Signup successful')
      return { data: response, error: null }
    } catch (error) {
      console.error('🔐 AuthAPI: Signup failed:', error)
      this.clearMemoryTokens()
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Sign in user
   */
  async signInWithPassword(email, password) {
    try {
      console.log('🔐 AuthAPI: Attempting signin for:', email)
      
      const response = await this.makeRequest('/auth-login', {
        method: 'POST',
        body: { email, password },
        skipAuth: true // No auth needed for login
      })

      // MFA required path - bubble up cleanly
      if (response?.mfa_required) {
        return { data: { mfa_required: true, mfa_token: response.mfa_token }, error: null }
      }

      // Save tokens
      if (response.session?.access_token) {
        this.saveTokensToStorage(
          response.session.access_token,
          response.session.refresh_token,
          response.user?.id,
          'custom'
        )
        
        // CRITICAL: Immediately trigger auth state change for UI update
        if (this._authCallback) {
          console.log('🔐 AuthAPI: Triggering auth state change after login')
          // Include user data in session object for the callback
          const sessionWithUser = {
            ...response.session,
            user: response.user
          }
          await this._authCallback('SIGNED_IN', sessionWithUser)
        }
      }

      console.log('🔐 AuthAPI: Signin successful')
      return { 
        data: { 
          user: response.user, 
          session: response.session 
        }, 
        error: null 
      }
    } catch (error) {
      console.error('🔐 AuthAPI: Signin failed:', error)
      this.clearMemoryTokens()
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Verify MFA during login in one call using email/password + code
   */
  async signInWithMfa(email, password, code) {
    try {
      const response = await this.makeRequest('/auth-mfa-login-verify', {
        method: 'POST',
        body: { email, password, code },
        skipAuth: true
      })

      if (response.session?.access_token) {
        this.saveTokensToStorage(response.session.access_token, response.session.refresh_token, response.user?.id, 'custom')
        if (this._authCallback) {
          const sessionWithUser = { ...response.session, user: response.user }
          await this._authCallback('SIGNED_IN', sessionWithUser)
        }
      }
      return { data: { user: response.user, session: response.session }, error: null }
    } catch (error) {
      this.clearMemoryTokens()
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Sign out user
   */
  async signOut() {
    try {
      console.log('🔐 AuthAPI: Attempting signout...')
      
      // Call API endpoint to invalidate server session
      await this.makeRequest('/auth-logout', {
        method: 'POST'
      })

      // Clear local tokens regardless of API response
      this.clearTokens()

      console.log('🔐 AuthAPI: Signout successful')
      return { error: null }
    } catch (error) {
      console.warn('🔐 AuthAPI: Signout API failed, clearing local tokens anyway:', error)
      
      // Still clear tokens even if API call failed
      this.clearTokens()
      return { error: null }
    }
  }

  /**
   * Enroll TOTP MFA for current user
   */
  async enrollTotp(password) {
    try {
      const options = { method: 'POST' }
      if (password) {
        options.body = { password }
      }
      const data = await this.makeRequest('/auth-mfa-totp-enroll', options)
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Verify TOTP enrollment with a 6-digit code
   */
  async verifyEnrollTotp(factorId, code) {
    try {
      const data = await this.makeRequest('/auth-mfa-totp-verify', {
        method: 'POST',
        body: { factor_id: factorId, code }
      })
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Disable all TOTP factors
   */
  async disableTotp() {
    try {
      const data = await this.makeRequest('/auth-mfa-totp-disable', {
        method: 'POST'
      })
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Get MFA status (totp enrolled, factors, and profile mirror)
   */
  async getMfaStatus() {
    try {
      const data = await this.makeRequest('/auth-mfa-status', {
        method: 'GET'
      })
      return { data, error: null }
    } catch (error) {
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Utility: build a QR code URL from an otpauth_url
   */
  buildQrFromOtpAuth(otpauthUrl, size = 220) {
    if (!otpauthUrl) return null
    const encoded = encodeURIComponent(otpauthUrl)
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encoded}`
  }

  /**
   * Get current session/user with network resilience
   */
  async getSession() {
    try {
      if (!this.token) {
        return { data: { session: null, user: null }, error: null }
      }

      console.log('🔐 AuthAPI: Validating current session...')
      
      const response = await this.makeRequest('/auth-session', {
        method: 'GET'
      })

      return { 
        data: { 
          session: { 
            access_token: this.token,
            refresh_token: this.refreshToken 
          }, 
          user: response.user 
        }, 
        error: null 
      }
    } catch (error) {
      console.error('🔐 AuthAPI: Session validation failed:', error)
      
      // CRITICAL: Only clear tokens on actual authentication failures, not network errors
      if (error.isNetworkError) {
        console.log('🔐 AuthAPI: Network error during session validation - keeping tokens for retry')
        
        // Return cached session data if we have tokens (offline mode)
        if (this.token && this.refreshToken) {
          return { 
            data: { 
              session: { 
                access_token: this.token,
                refresh_token: this.refreshToken 
              }, 
              user: null // We can't verify user without network, but we have tokens
            }, 
            error: { 
              message: 'Network connectivity issue - using cached session',
              isNetworkError: true 
            } 
          }
        }
      } else {
        // Clear tokens only on authentication errors (401, invalid tokens, etc.)
        console.log('🔐 AuthAPI: Authentication error - clearing invalid tokens')
        this.clearTokens()
      }
      
      return { data: { session: null, user: null }, error: { message: error.message } }
    }
  }

  /**
   * Get current user (alias for getSession for compatibility)
   */
  async getUser() {
    const sessionResult = await this.getSession()
    return {
      data: { user: sessionResult.data?.user || null },
      error: sessionResult.error
    }
  }

  /**
   * Refresh session tokens
   */
  async refreshSession() {
    try {
      if (!this.refreshToken) {
        throw new Error('No refresh token available')
      }

      console.log('🔐 AuthAPI: Refreshing session...')
      
      const response = await this.makeRequest('/auth-session', {
        method: 'POST',
        body: {
          access_token: this.token,
          refresh_token: this.refreshToken
        },
        skipAuth: true, // Don't use expired token for refresh
        skipTokenRefresh: true // Prevent infinite loops
      })

      // Save new tokens
      if (response.session?.access_token) {
        this.saveTokensToStorage(
          response.session.access_token,
          response.session.refresh_token,
          response.user?.id
        )
      }

      console.log('🔐 AuthAPI: Session refresh successful')
      return { 
        data: { 
          session: response.session, 
          user: response.user 
        }, 
        error: null 
      }
    } catch (error) {
      console.error('🔐 AuthAPI: Session refresh failed:', error)
      this.clearTokens()
      return { data: null, error: { message: error.message } }
    }
  }

  /**
   * Set up auth state change listener (mock for compatibility)
   * In API-based auth, we don't have real-time auth changes like Supabase
   */
  onAuthStateChange(callback) {
    // Store callback for potential future use
    this._authCallback = callback
    
    // Trigger initial state
    this.getSession().then(result => {
      const user = result.data?.user || null
      const session = result.data?.session || null
      
      if (user && session) {
        callback('SIGNED_IN', session)
      } else {
        callback('SIGNED_OUT', null)
      }
    })

    // Return unsubscribe function
    return {
      unsubscribe: () => {
        this._authCallback = null
      }
    }
  }

  /**
   * Reset password (placeholder - would need API endpoint)
   */
  async resetPasswordForEmail(email, options = {}) {
    // This would need a corresponding API endpoint
    console.warn('🔐 AuthAPI: resetPasswordForEmail not implemented yet')
    return { data: null, error: { message: 'Password reset not implemented' } }
  }

  /**
   * Update user password (placeholder - would need API endpoint)
   */
  async updateUser(updates) {
    // This would need a corresponding API endpoint
    console.warn('🔐 AuthAPI: updateUser not implemented yet')
    return { data: null, error: { message: 'User updates not implemented' } }
  }

  /**
   * Call a Supabase Edge Function
   * @param {string} functionName - Name of the Edge Function
   * @param {Object} data - Data to send to the function
   * @returns {Promise} Response from the function
   */
  async callFunction(functionName, data = {}) {
    try {
      console.log(`🔐 AuthAPI: Calling Edge Function: ${functionName}`)
      
      const response = await this.makeRequest(`/${functionName}`, {
        method: 'POST',
        body: data
      })

      console.log(`🔐 AuthAPI: Edge Function ${functionName} succeeded`)
      return { success: true, data: response }
    } catch (error) {
      console.error(`🔐 AuthAPI: Edge Function ${functionName} failed:`, error)
      return { success: false, error: error.message }
    }
  }

  // healthCheck method removed - no longer needed after migration to Supabase Edge Functions
}

// Create singleton instance
const authAPI = new AuthAPIClient()

export default authAPI

// Export as authAPIClient for compatibility with new services
export const authAPIClient = authAPI

// Export individual methods for compatibility
export const {
  signUp,
  signInWithPassword,
  signOut,
  getSession,
  getUser,
  onAuthStateChange,
  resetPasswordForEmail,
  updateUser
} = authAPI
